import { Suspense, lazy, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import '../pages/AppSuiteProjectsPage.css'
import AuthAware from '../../ultron-turing-manager/src/components/AuthAware'
import AppFactoryLayout, { type NavItem } from '../../ultron-turing-manager/src/components/AppFactoryLayout'
import AppSuiteLoginPage from '../pages/AppSuiteLoginPage'
import AppSuitePublishedAssetsPage from '../pages/AppSuitePublishedAssetsPage'
import AppSuiteUsersPage from '../pages/AppSuiteUsersPage'
import AppSuiteOverviewPage from '../pages/AppSuiteOverviewPage'
import AppSuiteTemplateThumbnailPage from '../pages/AppSuiteTemplateThumbnailPage'
import AppSuiteModalThumbnailPage from '../pages/AppSuiteModalThumbnailPage'
import AppSuiteProjectsPage from '../pages/AppSuiteProjectsPage'
import {
  getCredentialFromUserProfile,
  setCredentialInUserProfile,
  clearCredentialFromUserProfile,
} from '../services/appsuiteApi'
const AppSuiteProjectEditorPage = lazy(() => import('../pages/AppSuiteProjectEditorPage'))
const AppSuiteProjectModalEditorPage = lazy(() => import('../pages/AppSuiteProjectModalEditorPage'))
const AppSuiteRuntimePage = lazy(() => import('../pages/AppSuiteRuntimePage'))

const AppFactoryDeployPage = lazy(() => import('../../ultron-turing-manager/src/pages/appfactory/AppFactoryDeployPage'))
const AppFactoryDeviceTypeTemplatesPage = lazy(
  () => import('../../ultron-turing-manager/src/pages/appfactory/AppFactoryDeviceTypeTemplatesPage')
)
const ComponentStylesPage = lazy(() => import('../../ultron-turing-manager/src/pages/appfactory/ComponentStylesPage'))
const TemplateDetailPage = lazy(() => import('../../ultron-turing-manager/src/pages/appfactory/TemplateDetailPage'))
const AppFactoryPreviewPage = lazy(() => import('../../ultron-turing-manager/src/pages/appfactory/AppFactoryPreviewPage'))
const AppFactoryPreviewEmbedPage = lazy(
  () => import('../../ultron-turing-manager/src/pages/appfactory/AppFactoryPreviewEmbedPage')
)
const AppFactorySharePreviewPage = lazy(
  () => import('../../ultron-turing-manager/src/pages/appfactory/AppFactorySharePreviewPage')
)
const AppSuiteProjectSharePreviewPage = lazy(
  () => import('../../ultron-turing-manager/src/pages/appfactory/AppSuiteProjectSharePreviewPage')
)

const DEFAULT_TEMPLATE_PATH = '/appsuite/marketplace/templates'

// ── Tester Account (sidebar bottom) ──────────────────────────────────────
function TesterAccountSidebar() {
  const [collapsed, setCollapsed] = useState(true)
  const [input, setInput] = useState({ appId: '', apiKey: '' })
  const [saving, setSaving] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null)

  useEffect(() => {
    getCredentialFromUserProfile().then((c) => {
      if (c) setInput({ appId: c.appId, apiKey: c.apiKey })
    })
  }, [])

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok })
    setTimeout(() => setToast(null), 2500)
  }

  const handleSave = async () => {
    const appId = input.appId.trim()
    const apiKey = input.apiKey.trim()
    if (!appId && !apiKey) return
    setSaving(true)
    try {
      const result = await setCredentialInUserProfile(appId, apiKey)
      if (result.success) showToast('Saved', true)
      else showToast(result.error || 'Save failed', false)
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Save failed', false)
    } finally {
      setSaving(false)
    }
  }

  const handleClear = async () => {
    setClearing(true)
    try {
      const result = await clearCredentialFromUserProfile()
      if (result.success) {
        setInput({ appId: '', apiKey: '' })
        showToast('Cleared', true)
      } else {
        showToast(result.error || 'Clear failed', false)
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Clear failed', false)
    } finally {
      setClearing(false)
    }
  }

  return (
    <div className="appsuite-tester-account">
      <button
        type="button"
        className="appsuite-tester-account__header"
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <div className="appsuite-tester-account__header-left">
          <span className="material-symbols-rounded appsuite-tester-account__icon">key</span>
          <span className="appsuite-tester-account__title">Tester Account</span>
          {input.appId && collapsed ? (
            <span className="appsuite-tester-account__badge">{input.appId}</span>
          ) : null}
        </div>
        <span className="material-symbols-rounded appsuite-tester-account__chevron">
          {collapsed ? 'expand_more' : 'expand_less'}
        </span>
      </button>

      {!collapsed && (
        <div className="appsuite-tester-account__body">
          <div className="appsuite-tester-account__row">
            <label className="appsuite-tester-account__label">appId</label>
            <input
              type="text"
              className="appsuite-tester-account__input"
              value={input.appId}
              onChange={(e) => setInput((p) => ({ ...p, appId: e.target.value }))}
              placeholder="e.g. KLMGeDMy"
              disabled={saving}
            />
          </div>
          <div className="appsuite-tester-account__row">
            <label className="appsuite-tester-account__label">apiKey</label>
            <input
              type="password"
              className="appsuite-tester-account__input"
              value={input.apiKey}
              onChange={(e) => setInput((p) => ({ ...p, apiKey: e.target.value }))}
              placeholder="API Key"
              disabled={saving}
            />
          </div>
          <div className="appsuite-tester-account__actions">
            <button
              type="button"
              className="appsuite-tester-account__save"
              onClick={() => void handleSave()}
              disabled={saving || (!input.appId.trim() && !input.apiKey.trim())}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="appsuite-tester-account__clear"
              onClick={() => void handleClear()}
              disabled={clearing}
            >
              {clearing ? 'Clearing…' : 'Clear'}
            </button>
          </div>
          {toast ? (
            <div className={`appsuite-tester-account__toast${toast.ok ? '' : ' appsuite-tester-account__toast--err'}`}>
              {toast.msg}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function LegacyAppFactoryRedirect() {
  const location = useLocation()
  const nextPath = location.pathname.replace(/^\/appfactory/, '/appsuite')
  const next = `${nextPath}${location.search}${location.hash}`
  return <Navigate to={next} replace />
}

function LegacySectionRedirect({
  fromPrefix,
  toPrefix,
  keepSuffix = true,
}: {
  fromPrefix: string
  toPrefix: string
  keepSuffix?: boolean
}) {
  const location = useLocation()
  const suffix = location.pathname.startsWith(fromPrefix) ? location.pathname.slice(fromPrefix.length) : ''
  const nextPath = keepSuffix ? `${toPrefix}${suffix}` : toPrefix
  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<AppSuiteLoginPage />} />
          <Route path="appsuite/preview-link/:token" element={<AppFactorySharePreviewPage />} />
          <Route path="appsuite/project-preview/:token" element={<AppSuiteProjectSharePreviewPage />} />
          <Route path="appsuite/tmpl-preview/:templateId" element={<AppSuiteTemplateThumbnailPage />} />
          <Route path="appsuite/modal-preview/:modalId" element={<AppSuiteModalThumbnailPage />} />
          <Route path="appfactory/*" element={<LegacyAppFactoryRedirect />} />
          {/* /publishedapp/:appId — deployed end-user app (public, no AppSuite auth) */}
          <Route path="publishedapp/:appId" element={<AppSuiteRuntimePage />} />
          <Route element={<AuthAware />}>
            <Route path="appsuite" element={
              <AppFactoryLayout
                brandTitle="APPSUITE"
                basePath="/appsuite"
                sidebarBottom={<TesterAccountSidebar />}
                customNavItems={[
                  { to: '/appsuite', label: 'Overview', icon: 'dashboard', hasSubItems: false },
                  { to: '/appsuite/appsuite-iam', label: 'App Suite IAM', icon: 'admin_panel_settings', hasSubItems: false },
                  { to: '/appsuite/marketplace', label: 'Marketplace', icon: 'storefront', hasSubItems: true, subItems: [
                    { to: '/appsuite/marketplace/templates', label: 'App Templates', icon: 'view_quilt' },
                    { to: '/appsuite/marketplace/device-type-templates', label: 'Device Modal Templates', icon: 'devices' },
                  ] },
                  { to: '/appsuite/projects', label: 'My Projects', icon: 'folder_open', hasSubItems: false },
                ] satisfies NavItem[]}
              />
            }>
              <Route index element={<AppSuiteOverviewPage />} />
              <Route path="marketplace" element={<Outlet />}>
                <Route path="templates" element={<Outlet />}>
                  <Route index element={<AppSuitePublishedAssetsPage mode="templates" />} />
                  <Route path=":id" element={<TemplateDetailPage />} />
                </Route>
                <Route
                  path="device-type-templates"
                  element={<AppSuitePublishedAssetsPage mode="device-types" />}
                />
                <Route
                  path="components"
                  element={<ComponentStylesPage />}
                />
              </Route>
              <Route path="projects" element={<AppSuiteProjectsPage />} />
              <Route
                path="device-type-templates/*"
                element={
                  <LegacySectionRedirect
                    fromPrefix="/appsuite/device-type-templates"
                    toPrefix="/appsuite/marketplace/device-type-templates"
                  />
                }
              />
              <Route
                path="templates/*"
                element={
                  <LegacySectionRedirect
                    fromPrefix="/appsuite/templates"
                    toPrefix="/appsuite/marketplace/templates"
                  />
                }
              />
              <Route
                path="components/*"
                element={
                  <LegacySectionRedirect
                    fromPrefix="/appsuite/components"
                    toPrefix="/appsuite/marketplace/components"
                    keepSuffix={false}
                  />
                }
              />
              <Route path="deploy" element={<AppFactoryDeployPage />} />
              <Route path="appsuite-iam" element={<AppSuiteUsersPage />} />
              {/* OAuth test account page removed */}
            </Route>
            <Route path="appsuite/projects/:projectId/edit" element={<AppSuiteProjectEditorPage />} />
            <Route path="appsuite/projects/:projectId/modals/:deviceType" element={<AppSuiteProjectModalEditorPage />} />
            <Route path="appsuite/preview/:templateId" element={<AppFactoryPreviewPage />} />
            <Route path="appsuite/preview-embed/:templateId" element={<AppFactoryPreviewEmbedPage />} />
            <Route path="*" element={<Navigate to={DEFAULT_TEMPLATE_PATH} replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
